<template>
	<div class="policy-payment">
		<tw-title description="Please review the summary of cover below and make payment to complete your purchase"
			:email='paymentLoading === false'>Summary & Payment</tw-title>

		<div class="policy-payment-content-container confirm-details mb-20" :class="{ 'h-76': loading }">
			<div v-show="!loading">

				<div v-if="quote">
					<div class="quote-summary-container mt-5">
						<div class="policy-details">
							<div class="pb-2 border-b border-gray-200 mb-5 flex ">
								<div class="flex-1">
									<div class="text-2xl font-bold text-gray-700">
										Your quote
									</div>
									<p class="text-gray-500 font-light">

									</p>
								</div>
							</div>

							<dl>
								<div class="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
									<dt class="text-base leading-5 text-gray-500">
										Policy holder
									</dt>
									<dd class="mt-1 text-base leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
										<div>{{ quote.client.name }}</div>
									</dd>
								</div>
								<div class="mt-0 py-3 sm:grid sm:grid-cols-3 sm:gap-4">
									<dt class="text-base leading-5 text-gray-500">
										Address
									</dt>
									<dd class="mt-1 text-base leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
										<p class="address_part">{{ (quote.address.house) ? quote.address.house : '' }} {{
											(quote.address.street) ? quote.address.street : '' }}</p>
										<p class="address_part" v-if="quote.address.town">{{ quote.address.town }}</p>
										<p class="address_part" v-if="quote.address.county">{{ quote.address.county }}</p>
										<p class="address_part capitalize" v-if="quote.address.postcode">{{
											quote.address.postcode }}</p>
									</dd>
								</div>
								<div class="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
									<dt class="text-base leading-5 text-gray-500">
										Policy duration
									</dt>
									<dd class="mt-1 text-base leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
										{{ quote.coverStartFormat }} <i
											class="text-gray-400 fa fa-long-arrow-right mx-4"></i> {{ quote.coverEndFormat
											}}
									</dd>
								</div>
								<div class="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
									<dt class="text-base leading-5 text-gray-500">
										Bikes
									</dt>
									<dd class="mt-1 text-base leading-5 text-gray-900 sm:mt-0 sm:col-span-2">
										<ul class="border border-gray-200 rounded-md">
											<li class="pl-3 pr-4 py-3 flex items-center justify-between text-sm leading-5"
												v-for="bike in quote.covered">
												<div class="w-0 flex-1 flex items-center">
													<i class="fas fa-biking text-lg"></i>
													<span class="ml-3 flex-1 w-0 truncate">
														<span class="bike_make capitalize">{{ bike.make }}</span>
														<span class="divider">-</span>
														<span class="bike_model capitalize">{{ bike.model }}</span>
														<span class="divider">-</span>
														<span class="bike_model capitalize">{{ bike.value | currency
														}}</span>
													</span>
												</div>
											</li>
										</ul>
									</dd>
								</div>
							</dl>
						</div>
						<div class="policy-options mt-20">

							<div class="pb-2 border-b border-gray-200 mb-5 mt-6 flex ">
								<div class="flex-1">
									<div class="text-2xl font-bold text-gray-700">
										Cover summary
									</div>
									<p class="text-gray-500 font-light">

									</p>
								</div>
							</div>

							<div class="all-options">
								<div class="policy-section">
									<p class="policy-option">Policy Products:</p>
									<div class="product" v-if="unselectableProducts">
										<div class="optional" v-for="(p, key) in unselectableProducts" v-if="p.option">
											<p class="policy-optional">{{ p.name }}</p>
											<p class="policy-price">{{ p.option.value | currency }}</p>
										</div>
									</div>
									<p class="none-selected" v-else>None Selected</p>

									<p class="policy-option mt-3">Additional Products:</p>
									<div class="product" v-if="selectedProducts && numSelected > 0">
										<div class="optional" v-for="(p, key) in selectedProducts" v-if="p.option">
											<p class="policy-optional">{{ p.name }}</p>
											<p class="policy-price">{{ p.option.value | currency }}</p>
										</div>
									</div>
									<p class="none-selected" v-else>None Selected</p>
								</div>

								<div class="policy-section">
									<div v-if="quote.discounts.length > 0">
										<p class="policy-option mt-3">Discounts:</p>
										<div class="space-y-3">
											<div class="discount" v-for="discount in quote.discounts">
												<div class="discount-container">
													<div class="flex gap-2 items-center">
														<div class="flex-1">
															<div class="font-light text-base text-teal-700">
																<span class="font-medium uppercase"
																	v-if='discount.owner_type !== "system"'>
																	{{ discount.code }} -
																</span>
																<span class="font-normal">
																	<span v-if='discount.owner_type === "system"'>
																		<i class="fas fa-star text-teal-500"></i>
																	</span>
																	{{ discount.name_human }}
																</span>
															</div>
															<div class="text-sm leading-2"
																v-if='discount.owner_type !== "system"'>
																<a v-tw-modal:removeDiscount
																	class="whitespace-no-wrap font-medium text-teal-700 hover:text-teal-600 transition ease-in-out duration-150 cursor-pointer"
																	@click="selectDiscount(discount)">
																	Remove
																</a>
															</div>
														</div>
														<div class="text-right flex flex-row-reverse">
															<div>
																<div
																	class="font-semibold self-center text-lg text-gray-600">
																	- {{ discount.value }}
																</div>
															</div>
														</div>

													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div class="policy-section border-t border-b border-gray-200 py-2">
									<div class="price">

										<div class="text-gray-700 text-xl font-semibold">
											Total Premium
											<div class="text-sm text-gray-500 font-light">Inc IPT at 12%</div>
										</div>


										<p class="policy-price mt-2">{{ quote.client_premium | currency }}</p>
									</div>
								</div>

								<div class="policy-section">
									<div class="discounts">

										<div class="t">
											<tw-button @clicked="discounts.showInput = !discounts.showInput"
												:full-width='false' variant='ghost' align=''>
												Got a discount code?

												<i class="fas fa-caret-right ml-3" v-if="!discounts.showInput"></i>
												<i class="fas fa-caret-down ml-3" v-else></i>
											</tw-button>
										</div>


										<div class="discounts-container" v-if="quote">
											<div class="policy-payment-content">
												<div class="discounts-feedback mt-3"
													v-if="(discounts.feedback) ? true : false">
													<tw-alert :show="(discounts.feedback) ? true : false"
														:type="discounts.feedback.type">{{ discounts.feedback.message
														}}</tw-alert>
												</div>
												<div class="discounts-form">
													<div v-if='discounts.showInput'>
														<div class="flex gap-3">

															<input type="text" v-model="form.discountCode"
																placeholder="Discount Code">

															<tw-button align='' :disabled="discounts.loading"
																@clicked="addDiscount">
																{{ (!discounts.loading) ? 'Apply' : 'Loading..' }}
															</tw-button>

														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="pb-2 border-b border-gray-200 mt-12 mb-5 flex ">
					<div class="flex-1">
						<div class="text-2xl font-bold text-gray-700">
							Purchase method
						</div>
						<p class="text-gray-500 font-light">
							How would you like to pay?
						</p>
					</div>
				</div>
				<div class="subscription-container" v-if="quote">
					<tw-toggle-group name="subscription_type" :options="subscriptionTypes" :selected="quote.type"
						@updated="updateSubscription" :loading="subscriptionLoading" class="radio">
						<template v-for="(opt, index) in subscriptionTypes" :slot="'additional_'+index">
							<p class="option-value">{{ opt.value | currency }}</p>
						</template>
					</tw-toggle-group>
					<!-- <tw-toggle-group name="offers" :options="offersOptions" @updated="offersUpdated" :selected="selectedOffer" class="grid grid-cols-1 sm:grid-cols-2 gap-4 radio"></tw-toggle-group> -->
				</div>


				<div class="pb-2 border-b border-gray-200 mt-12 mb-5 flex ">
					<div class="flex-1">
						<div class="text-2xl font-bold text-gray-700">
							Card details
						</div>
						<p class="text-gray-500 font-light">
							Please provide your card details
						</p>
					</div>
				</div>
				<div class="policy-payment-content">
					<div class="mb-2" v-if="paymentMethods.errors || paymentMethods.success">
						<tw-alert type="error" :show="(paymentMethods.errors) ? true : false"
							@close="paymentMethods.errors = false">{{ paymentMethods.errors }}</tw-alert>
						<tw-alert :show="(paymentMethods.success) ? true : false" @close="paymentMethods.success = false">{{
							paymentMethods.success }}</tw-alert>
					</div>

					<div class="payment-methods-container" :class="{ 'h-40': paymentMethods.loading }">
						<div v-show="!paymentMethods.loading">
							<div class="existing-cards" v-if="paymentMethods.data && paymentMethods.data.length > 0">
								<ul class="existing-cards-container">
									<li :class="{ 'default': method.is_default }"
										v-for="(method, index) in paymentMethods.data">
										<div class="existing-card-title">
											<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
												<path
													d="M18 6V4H2v2h16zm0 4H2v6h16v-6zM0 4c0-1.1.9-2 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm4 8h4v2H4v-2z" />
											</svg>
											<div class="pl-5 flex-1 truncate">
												<div class="existing-card-title-container">
													<h3>Card</h3>
													<span class="ml-2" v-if="method.is_default">Default</span>
												</div>
												<p class="existing-card-number">
												<div class="capitalize">
													{{ method.card.brand }} •••• {{ method.card.last4 }}
												</div>
												</p>
											</div>
										</div>
										<div class="existing-card-button-container">
											<div class="-mt-px flex">
												<div class="w-0 flex-1 flex border-r border-gray-200">
													<a class="delete-btn no-underline" @click="deletePaymentMethod(method)">
														<span class="ml-3">
															<i class="fas fa-trash-alt"></i>
															<span class="ml-3">
																<span>Delete</span>
															</span>
														</span>
													</a>
												</div>
											</div>
										</div>
									</li>
								</ul>

								<div class="relative my-12 w-full mx-auto" v-show="!paymentMethods.added">
									<div class="absolute inset-0 flex items-center">
										<div class="w-full border-t border-gray-300"></div>
									</div>
									<div class="relative flex justify-center text-sm leading-5">
										<span class="px-2 bg-white text-gray-500">
											Or setup a new card
										</span>
									</div>
								</div>
							</div>


							<div class="payment-method-input-container mb-32" v-show="!paymentMethods.added">
								<div class="w-full">

									<div class="mt-1 bg-white rounded-md shadow-sm w-full">
										<div>
											<input class="rounded-t-md" autocomplete="off" type="text"
												aria-label="Cardholder name" id="card-name" v-model="form.payment.name"
												placeholder="Full name on card" />
										</div>
										<div>
											<div aria-label="Card number" id="card-number"
												class="form-input relative block w-full rounded-none bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
												placeholder="Card number">
											</div>
										</div>
										<div class="-mt-px flex">
											<div class="w-1/2 flex-1 min-w-0">
												<div aria-label="Card expiration date" id="card-expiry"
													class="form-input relative block w-full rounded-none bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
													placeholder="MM / YY">
												</div>
											</div>
											<div class="-ml-px flex-1 min-w-0">
												<div aria-label="Card CVC" id="card-cvc"
													class="form-input relative block w-full bg-transparent rounded-none focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
													placeholder="CVC">
												</div>
											</div>
										</div>
										<div class="-mt-px flex">
											<div class="w-1/2 flex-1 min-w-0">
												<input class="rounded-b-md" autocomplete="off" type="text"
													aria-label="Postcode" id="card-postcode" v-model="form.payment.postcode"
													placeholder="Postcode" />
											</div>
										</div>
									</div>
									<div class="text-base flex gap-2 items-center text-gray-600 mt-2">
										<div class="flex items-center gap-2 flex-1">
											<i class="fa fa-shield text-lg tex-gray-700"></i> Secure checkout
										</div>
										<div class="justify-self-end flex items-center gap-2">

											<div class="font-light">
												Powered by
											</div>
											<div>
												<i class="fab fa-cc-stripe text-4xl"></i>
											</div>
										</div>
									</div>
								</div>

							</div>
						</div>
						<loading :show="paymentMethods.loading"></loading>
					</div>
				</div>
			</div>
			<loading :show="loading" :message="loadingMessage" :subtext="loadingSubtext"></loading>
		</div>

		<tw-modal ref="removeDiscount" size="lg" @ok="removeDiscount" ok-text="delete" :padding="false">
			<template v-slot:header>
				Delete <span class="italic capitalize">{{ (discounts.selected && discounts.selected.code) ?
					discounts.selected.code : 'Untitled code' }}</span>
			</template>

			<template v-slot:subtext>
				Deleting this discount will remove it from your quote.
			</template>

			<template v-slot:body>
				<p class="text-gray-500">Are you sure you want to delete this discount?</p>
			</template>

		</tw-modal>
	</div>
</template>

<script>
// Create a Stripe client.
let stripe;
let cardNumber, cardExpiry, cardCvc;

export default {
	name: 'quote.payment',
	props: {
		quote: {
			required: true,
		},
		paymentLoading: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			loading: false,
			isLoading: true,
			postcodeSearch: null,
			form: {
				discountCode: null,
				house: null,
				street: null,
				town: null,
				county: null,
				postcode: null,
				payment: {
					name: null,
					cardNumber: null,
					intent: null,
					postcode: null,
				}
			},
			paymentMethods: {
				data: false,
				loading: false,
				errors: false,
				success: false,
				added: false,
			},
			discounts: {
				loading: false,
				feedback: false,
				selected: false,
				showInput: false
			},
			subscriptionLoading: false,
			loadingMessage: false,
			loadingSubtext: false,
		}
	},

	computed: {
		hasSelectableProducts() {
			let hasProducts = false;

			if (this.selectableProducts) {
				Object.keys(this.selectableProducts).forEach((prod_key) => {
					if (this.selectableProducts[prod_key].option) {
						hasProducts = true;
					}
				});
			}

			return hasProducts;
		},
		selectableProducts() {
			var products = {};

			const self = this;
			Object.keys(self.quote.products.products).forEach(function (key) {
				if (self.quote.products.products[key].meta.is_selectable) {
					products[key] = self.quote.products.products[key].meta;

					// get selected product
					let options = self.quote.products.products[key].options;
					let selected = options.filter(option => option.is_selected == true);
					products[key].option = selected[0];
				}
			});

			return products;
		},
		unselectableProducts() {
			var products = {};

			const self = this;
			Object.keys(self.quote.products.products).forEach(function (key) {
				let productData = self.quote.products.products[key].meta;

				// get selected product (if selectable by user)
				if (!productData.is_selectable) {
					products[key] = productData;

					let options = self.quote.products.products[key].options;
					let selected = options.filter(option => option.is_selected == true);
					products[key].option = selected[0];
				}
			});

			return products;
		},
		subscriptionTypes() {
			return [
				{
					name: 'annually',
					value: this.quote.client_premium
				},
				{
					name: 'monthly',
					value: (this.quote.client_premium / 12)
				}
			];
		},
		defaultPaymentMethod() {
			var defaultMethod = false;

			if (this.paymentMethods.data.length > 0) {
				defaultMethod = this.paymentMethods.data.filter(method => method.is_default);

				if (defaultMethod) {
					defaultMethod = defaultMethod[0];
				}
			}

			return defaultMethod;
		},
		selectedProducts() {
			var products = {};

			const self = this;
			Object.keys(self.quote.products.products).forEach(function (key) {
				let productData = self.quote.products.products[key].meta;

				// get selected product
				let options = self.quote.products.products[key].options;
				let selected = options.filter(option => option.is_selected == true);
				if (productData.is_selectable) {
					if (selected[0].value > 0) {
						products[key] = productData;
						products[key].option = selected[0];
					}
				}
			});

			return products;
		},
		numSelected() {
			return Object.keys(this.selectedProducts).length;
		},
		isComplete() {


			return true;
		}
	},
	mounted() {

		if (this.quote.client === null) {
			this.$router.push({ name: 'quote.contact' });
			return;
		}

		this.$emit('refresh', { passed: true });

		this.stripeSetup();
	},
	watch: {
		// quote: function (newQuote, oldQuote) {
		// 	this.$emit('refresh', {passed: this.isComplete});

		// 	this.load();
		// },
	},
	methods: {
		load(load = true) {
			const self = this;

		},
		stripeSetup() {
			const self = this;
			this.loading = true;

			axios.get('quote/stripe')
				.then((resp) => {
					if (resp.data.success) {
						stripe = Stripe(resp.data.stripe);


						let elements = stripe.elements();

						let style = {
							base: {
								color: '#32325d',
								fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
								fontSmoothing: 'antialiased',
								fontSize: '16px',
								'::placeholder': {
									color: '#aab7c4'
								}
							},
							invalid: {
								color: '#fa755a',
								iconColor: '#fa755a'
							}
						};

						cardNumber = elements.create('cardNumber', {
							style: style
						});
						cardExpiry = elements.create('cardExpiry', {
							style: style
						});
						cardCvc = elements.create('cardCvc', {
							style: style
						});
						// cardPostcode = elements.create('postalCode', {
						// 	placeholder: 'Postcode',
						// 	style: style
						// });

						cardNumber.mount('#card-number');
						cardExpiry.mount('#card-expiry');
						cardCvc.mount('#card-cvc');
						// cardPostcode.mount('#card-postcode');

					}
					this.loading = false;

				})
				.catch(error => {
				});
		},
		discountUpdated(e) {
			if (e.discount_code) {
				this.form.discountCode = e.discount_code.value;
			}
		},
		selectDiscount(d) {
			this.discounts.selected = Object.assign({}, d);
		},
		addDiscount() {
			const self = this;

			// show loading gif
			self.$emit('loading', true);

			axios.post('quote/discount', { discountCode: self.form.discountCode })
				.then((resp) => {
					// stop showing loading gif
					self.$emit('loading', false);

					if (resp.data.success) {
						// push back quote to parent
						self.$emit('refresh', { quote: resp.data.quote });

						// show discount feedback
						self.discounts.feedback = {
							type: 'success',
							message: resp.data.message
						}
					} else {
						// show discount feedback
						self.discounts.feedback = {
							type: 'error',
							message: resp.data.message
						};
					}
				})
				.catch(error => {
					// stop showing loading gif
					self.$emit('loading', false);

					// show discount feedback
					self.discounts.feedback = {
						type: 'error',
						message: error
					};
				});
		},
		removeDiscount() {
			const self = this;

			// show loading gif
			self.$emit('loading', true);

			axios.delete('quote/discount/' + this.discounts.selected.id)
				.then((resp) => {
					// stop showing loading gif
					self.$emit('loading', false);

					if (resp.data.success) {
						// push back quote to parent
						self.$emit('refresh', { quote: resp.data.quote });

						// show discount feedback
						self.discounts.feedback = {
							type: 'success',
							message: 'Discount has been removed from quote'
						};
					} else {
						// show discount feedback
						self.discounts.feedback = {
							type: 'error',
							message: 'An error occurred'
						};
					}
				})
				.catch(error => {
					// stop showing loading gif
					self.$emit('loading', false);

					// show discount feedback
					self.discounts.feedback = {
						type: 'error',
						message: error
					};
				});
		},
		cardFeedback(type, message) {
			const self = this;

			self.paymentMethods[type] = message;
		},
		valid_postcode(postcode) {
			postcode = postcode.replace(/\s/g, "");
			var regex = /[A-Z]{1,2}[0-9]{1,2} ?[0-9][A-Z]{2}/i;

			return regex.test(postcode);
		},

		sendToGA() {

			dataLayer.push({ ecommerce: null });
			dataLayer.push({
				event: "purchase",
				ecommerce: {
					transaction_id: this.quote.reference,
					value: this.quote.client_premium,
					tax: 0,
					shipping: 0,
					currency: "GBP",
					coupon: "",
					items: [
						{
							item_id: this.quote.reference,
							item_name: "Insurance",
							affiliation: "Online Quote Process",
							coupon: "",
							discount: 0,
							index: 0,
							item_brand: "",
							item_category: "",
							item_category2: "",
							item_category3: "",
							item_category4: "",
							item_category5: "",
							item_list_id: "",
							item_list_name: "",
							item_variant: "",
							location_id: "",
							price: this.quote.client_premium,
							quantity: 1
						}]
				}
			});

		},
		policyIntent(premiumCheck) {

			let data = {};

			if (premiumCheck) {
				data = {
					client_premium: this.quote.client_premium
				};
			}

			return Vue.axios.post('quote/policy-intent', data);
		},
		submit(premiumCheck = true) {
			const self = this;


			if (this.cardIsValid() == false) {
				this.cardFeedback('errors', this.cardInvalidMessage(), true);
				return;
			}


			// Disable pay now
			this.$emit('paymentLoading', true);

			// show loading gif
			self.loading = true;
			self.loadingMessage = 'Validating your policy...'
			self.loadingSubtext = 'Please wait while we validate your policy… This may take a moment and whilst you are waiting please do not close your browser or click on the back button.';


			this.policyIntent(premiumCheck).then(response => {

				// Validation failed
				if (response.data.success === false) {

					self.$emit('paymentLoading', false);


					if (response.data.hasOwnProperty('price_matching') && response.data.price_matching == false) {
						self.loading = false;
						throw new Error('Price does not match');
						return;
					} else {
						throw new Error(response.data.message.message ?? response.data.message);
					}

					return;
				}

				// Pass payment intent to stripe and charge
				self.processPayment(response.data.intent).then(result => {

					if (result.error) {
						self.$emit('paymentLoading', false);
						self.loading = false;
						self.cardFeedback('errors', result.error.message, true);
						self.$emit('feedback', {
							'type': 'error',
							'message': "An error occurred",
							'alwaysShow': true,
						});
					} else {
						// Payment has been made... process policy
						if (result.paymentIntent.status === 'succeeded') {
							self.processPolicy();
						}
					}
				}).catch(error => {

					self.loading = false;
					self.$emit('paymentLoading', false);

					self.$emit('feedback', {
						'type': 'error',
						'message': "An error occurred. Please review your payment details and ensure everything is correct.",
						'alwaysShow': true,
					});

				});

			}).catch(error => {

				self.loading = false;
				self.$emit('paymentLoading', false);

				self.$emit('feedback', {
					'type': 'error',
					'message': error.hasOwnProperty('message') ? error.message : 'An error occurred',
					'alwaysShow': true,
				});

			});


		},
		processPayment(intent) {
			const self = this;

			// clear any existing errors
			// self.$emit('feedback', []);

			// show loading gif
			self.loading = true;
			self.loadingMessage = 'Processing payment...'
			self.loadingSubtext = 'Please wait while we process your payment… This may take a moment and whilst you are waiting please do not close your browser or click on the back button.';

			return stripe.confirmCardPayment(intent.client_secret, {
				payment_method: {
					card: cardNumber,
					billing_details: {
						name: this.form.payment.name,
						address: {
							// line1: self.address.street,
							// city: self.address.town,
							// state: self.address.county,
							// country: self.address.country,
							postal_code: this.form.payment.postcode.toUpperCase(),
						}
					}
				},
				setup_future_usage: 'off_session'
			});

		},
		processPolicy(premiumCheck) {

			const self = this;

			let usesWebHook = true
			if (usesWebHook) {
				this.analytics();

				axios.post('quote/purchase');

				this.$emit('next', true);
				return;
			}


			// clear any existing errors
			// self.$emit('feedback', []);

			// show loading gif
			self.loading = true;
			self.loadingMessage = 'Processing your policy...'
			self.loadingSubtext = 'Please wait while we process your policy… This may take a moment and whilst you are waiting please do not close your browser or click on the back button.';

			// Only add client client if premium check needs to happen
			let postData = {};
			if (premiumCheck) {
				postData = { client_premium: self.quote.client_premium }
			}

			// purchase policy
			axios.post('quote/purchase', postData)
				.then((resp) => {
					if (resp.data) {
						// stop loading gif
						self.loading = false;

						// Send Data to Google Analytics
						self.analytics();

						if (resp.data.success) {
							// go to next step
							self.$emit('next', true);
						} else {
							if (resp.data.message) {
								// feedback message
								self.$emit('feedback', {
									'type': 'error',
									'message': "An error occurred. Please review your quote and ensure everything is correct.",
									'alwaysShow': true,
								});

								self.cardFeedback('errors', resp.data.message, true);
							} else {
								self.$emit('feedback', {
									'type': 'error',
									'message': "An error occurred whilst processing your payment. Please try again.",
									'alwaysShow': true,
								});
							}
						}
					}
				})
				.catch(error => {
					// stop loading gif
					self.loading = false;
					self.$emit('paymentLoading', false);

					// show feedback
					self.$emit('feedback', {
						'type': 'error',
						'message': error,
					});
				});
		},
		cardIsValid() {
			return this.cardInvalidMessage() === null;
		},
		cardInvalidMessage() {

			if (!this.form.payment.name) {
				return 'Please enter a valid cardholder name';
			}

			if (typeof cardNumber !== 'undefined' && (cardNumber._empty || cardNumber._invalid)) {
				return 'Please enter a valid card number';
			}

			if (typeof cardExpiry !== 'undefined' && (cardExpiry._empty || cardExpiry._invalid)) {
				return 'Please enter a valid card expiry date';
			}

			if (typeof cardCvc !== 'undefined' && (cardCvc._empty || cardCvc._invalid)) {
				return 'Please enter a valid card CVC';
			}

			if (!this.form.payment.postcode) {
				return 'Please enter a valid postcode';
			}


			const quotePostcode = this.quote.address.postcode ? this.quote.address.postcode.toLowerCase().replace(/ /g, '') : "";
			const billingPostcode = this.form.payment.postcode ? this.form.payment.postcode.toLowerCase().replace(/ /g, '') : "";

			if (billingPostcode !== quotePostcode) {
				return "Billing postcode must be the same as the policyholder's postcode";
			}

			// if (typeof cardPostcode !== 'undefined' && (cardPostcode._empty || cardPostcode._invalid)) {
			// 	return 'Please enter a valid postcode';
			// }

			return null;
		},


		updateSubscription(e) {
			let subscriptionType = e.name;

			// start loading gif
			this.subscriptionLoading = true;

			// refresh step progress
			this.$emit('refresh', { passed: this.isComplete });

			const self = this;
			axios.post('quote/subscriptionType', { type: subscriptionType })
				.then((resp) => {

					self.subscriptionLoading = false;

					self.$emit('refresh', { passed: this.isComplete });
				})
				.catch(error => {
					self.subscriptionLoading = false;

					self.$emit('feedback', {
						'type': 'error',
						'message': error
					});
				})
		},
		analytics() {

			// Google
			this.sendToGA();

			// Bing
			uet_report_conversion();

			// Veritonic
			let quote = this.quote;
			(function (w, d, quote) {

				var id = "vpr-capture", n = "script"

				if (!d.getElementById(id)) {
					vpr = w.vpr || function () { (w.vpr.a = w.vpr.a || []).push(arguments); };
					var e = d.createElement(n); e.id = id; e.async = 1
					e.src = 'https://cdn.veritonic.com/static/vpr-eu.min.js'
					var s = d.getElementsByTagName(n)[0]
					s.parentNode.insertBefore(e, s)

				}

				vpr("veritonic_id", "pgbx2ZvU"); // this is the Veritonic ID associated with your brand
				vpr('purchase', { // this is the action you want to track
					// update your cart and product details below for each purchase you want to track with this action
					value: quote.client_premium,
					currency: 'GBP',
					discount_code: null,
					quantity: 1,
					line_items: [
						{
							value: quote.client_premium,
							quantity: 1,
							product_id: quote.reference,
							product_name: 'Cycle insurance',
							product_type: 'insurance',
							product_vendor: 'ProtectMyBike',
							variant_id: null,
							variant_name: null
						}
					]
				});

			})(window, document, quote);
		}
	}
}
</script>